import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, ScrollText, ListChecks, Users, MapPin,
  Film, Image, Sparkles, ClipboardList, MessageSquare, Settings, LogOut,
  Coins, HelpCircle, Package, Network, CreditCard, LayoutList, Radar
} from "lucide-react";
import { BRAND } from "@/constants/brand";
import { useAuth } from "@/context/AuthContext";

const primaryNav = [
  { to: "/app", icon: LayoutDashboard, label: "Home", exact: true },
  { to: "/app/projects", icon: FolderOpen, label: "Projects" },
];

const projectNav = (pid) => [
  { to: `/app/projects/${pid}/script`, icon: ScrollText, label: "Script" },
  { to: `/app/projects/${pid}/beats`, icon: LayoutList, label: "Beat Board" },
  { to: `/app/projects/${pid}/scenes`, icon: ListChecks, label: "Scenes" },
  { to: `/app/projects/${pid}/characters`, icon: Users, label: "Characters" },
  { to: `/app/projects/${pid}/relationships`, icon: Network, label: "Relationships" },
  { to: `/app/projects/${pid}/locations`, icon: MapPin, label: "Locations" },
  { to: `/app/projects/${pid}/storyboards`, icon: Film, label: "Storyboards" },
  { to: `/app/projects/${pid}/ai`, icon: Sparkles, label: "AI Studio" },
  { to: `/app/projects/${pid}/continuity`, icon: Radar, label: "Continuity" },
  { to: `/app/projects/${pid}/production`, icon: ClipboardList, label: "Production" },
  { to: `/app/projects/${pid}/assets`, icon: Image, label: "Assets" },
];

const bottomNav = [
  { to: "/app/community", icon: MessageSquare, label: "Community" },
  { to: "/app/billing", icon: CreditCard, label: "Billing" },
  { to: "/app/settings", icon: Settings, label: "Settings" },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const projectMatch = location.pathname.match(/^\/app\/projects\/([^/]+)/);
  const activeProjectId = projectMatch ? projectMatch[1] : null;

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-[#FFFFF0]">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-[#1E1E1E] bg-[#0F0F10] flex flex-col" data-testid="dashboard-sidebar">
        <Link to="/app" className="h-16 px-5 flex items-center gap-3 border-b border-[#1E1E1E]">
          <div className="w-7 h-7 border border-[#D4AF37] flex items-center justify-center">
            <div className="w-2 h-2 bg-[#D4AF37]"></div>
          </div>
          <span className="font-editorial text-lg">{BRAND.short}<span className="text-[#D4AF37]"> AI</span></span>
        </Link>

        <nav className="flex-1 px-3 py-5 space-y-6 overflow-y-auto">
          <div className="space-y-1">
            {primaryNav.map((n) => (
              <SideLink key={n.to} to={n.to} icon={n.icon} label={n.label} end={n.exact} />
            ))}
          </div>

          {activeProjectId && (
            <div>
              <div className="px-3 mb-2 text-[10px] tracking-widest uppercase text-[#71717A]">Project</div>
              <div className="space-y-1">
                {projectNav(activeProjectId).map((n) => (
                  <SideLink key={n.to} to={n.to} icon={n.icon} label={n.label} />
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="px-3 mb-2 text-[10px] tracking-widest uppercase text-[#71717A]">Workspace</div>
            <div className="space-y-1">
              {bottomNav.map((n) => (
                <SideLink key={n.to} to={n.to} icon={n.icon} label={n.label} />
              ))}
            </div>
          </div>
        </nav>

        <div className="p-3 border-t border-[#1E1E1E] space-y-2">
          <div className="flex items-center justify-between px-2 py-1.5 text-xs text-[#A1A1AA]">
            <span className="flex items-center gap-1.5"><Coins size={12} className="text-[#D4AF37]" />Credits</span>
            <span className="font-mono text-[#FFFFF0]" data-testid="sidebar-credits">{user?.credits ?? 0}</span>
          </div>
          <button
            onClick={() => { logout(); navigate("/"); }}
            className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[#A1A1AA] hover:text-[#FFFFF0] hover:bg-[#1E1E1E] transition-colors"
            data-testid="sidebar-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-[#1E1E1E] bg-[#0A0A0A] flex items-center justify-between px-6" data-testid="dashboard-topbar">
          <div className="text-sm text-[#71717A] tracking-wide uppercase">
            {location.pathname.replace("/app", "") || "Home"}
          </div>
          <div className="flex items-center gap-4">
            <button className="text-[#71717A] hover:text-[#FFFFF0]" title="Help" data-testid="help-btn"><HelpCircle size={16}/></button>
            <div className="h-8 w-8 rounded-full bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center text-xs font-medium" data-testid="user-avatar">
              {user?.name?.[0]?.toUpperCase() ?? "?"}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

function SideLink({ to, icon: Icon, label, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
          isActive
            ? "bg-[#1E1E1E] text-[#FFFFF0] border-l-2 border-[#D4AF37] pl-[10px]"
            : "text-[#A1A1AA] hover:text-[#FFFFF0] hover:bg-[#141414]"
        }`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon size={15} strokeWidth={1.5} />
      <span>{label}</span>
    </NavLink>
  );
}
